import { Trans } from "react-i18next";
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { toast } from "react-toastify";
import apiClient from "@shared/services/apiClient";
import { FilterOverride } from "./customizationTypes";
import {
  getErrorMessage,
  toDateTimeLabel,
  unwrapApiData,
} from "./customizationUtils";
import { useRecordingChannels } from "./customizationHooks";
import "./customizationPanels.css";

type FilterOverrideEditorPanelProps = {
  recordingId: string;
};

const FilterOverrideEditorPanel: React.FC<FilterOverrideEditorPanelProps> = ({
  recordingId,
}) => {
  const [rows, setRows] = useState<FilterOverride[]>([]);
  const [loading, setLoading] = useState(false);
  const [channelName, setChannelName] = useState("");
  const { channels, loading: channelsLoading } =
    useRecordingChannels(recordingId);
  const [hp, setHp] = useState("");
  const [lp, setLp] = useState("");
  const [notch, setNotch] = useState("");

  const loadOverrides = useCallback(async () => {
    if (!recordingId.trim()) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const response = await apiClient.get(
        `/eeg/recordings/${recordingId.trim()}/channel-filters`,
      );
      const data = unwrapApiData<FilterOverride[]>(response);
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [recordingId]);

  useEffect(() => {
    loadOverrides();
  }, [loadOverrides]);

  const upsertOverride = async () => {
    if (!recordingId.trim()) {
      toast.error("Recording ID is required.");
      return;
    }
    if (!channelName.trim()) {
      toast.error("Channel name is required.");
      return;
    }

    const parsedHp = hp.trim() ? Number(hp.trim()) : null;
    const parsedLp = lp.trim() ? Number(lp.trim()) : null;
    const parsedNotch = notch.trim() ? Number(notch.trim()) : null;

    if (parsedHp !== null && Number.isNaN(parsedHp)) {
      toast.error("HP must be a number.");
      return;
    }
    if (parsedLp !== null && Number.isNaN(parsedLp)) {
      toast.error("LP must be a number.");
      return;
    }
    if (parsedNotch !== null && Number.isNaN(parsedNotch)) {
      toast.error("Notch must be a number.");
      return;
    }

    try {
      await apiClient.put(
        `/eeg/recordings/${recordingId.trim()}/channel-filters/${encodeURIComponent(channelName.trim())}`,
        {
          hp: parsedHp,
          lp: parsedLp,
          notch: parsedNotch,
        },
      );
      toast.success(`Filter override saved for ${channelName.trim()}.`);
      setChannelName("");
      setHp("");
      setLp("");
      setNotch("");
      await loadOverrides();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const deleteOverride = async (targetChannelName: string) => {
    if (!recordingId.trim()) return;
    try {
      await apiClient.delete(
        `/eeg/recordings/${recordingId.trim()}/channel-filters/${encodeURIComponent(targetChannelName)}`,
      );
      toast.success(`Filter override removed for ${targetChannelName}.`);
      await loadOverrides();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <Card className="eeg-customization-panel">
      <Card.Body>
        <h5 className="eeg-customization-panel__title">
          <Trans ns="features" i18nKey="ui.copy.fec111e4e211" />
        </h5>
        {!recordingId.trim() && (
          <Alert variant="warning">
            <Trans ns="features" i18nKey="ui.copy.3fac9e884759" />
          </Alert>
        )}
        <Row className="g-2 eeg-customization-panel__form-row">
          <Col md={3}>
            <Form.Select
                size="sm"
                value={channelName}
                onChange={(event) => setChannelName(event.target.value)}
                disabled={channelsLoading || channels.length === 0}
              >
                <option value=""><Trans ns="features" i18nKey="ui.copy.910859289419" /></option>
                {channels.map((ch) => (
                  <option key={ch} value={ch}>
                    {ch}
                  </option>
                ))}
              </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Control
              size="sm"
              value={hp}
              onChange={(event) => setHp(event.target.value)}
              placeholder="High-pass cutoff (Hz)"
            />
          </Col>
          <Col md={2}>
            <Form.Control
              size="sm"
              value={lp}
              onChange={(event) => setLp(event.target.value)}
              placeholder="Low-pass cutoff (Hz)"
            />
          </Col>
          <Col md={2}>
            <Form.Control
              size="sm"
              value={notch}
              onChange={(event) => setNotch(event.target.value)}
              placeholder="Notch (50/60)"
            />
          </Col>
          <Col md={3} className="d-grid">
            <Button size="sm" onClick={upsertOverride} disabled={!recordingId.trim()}>
              <Trans ns="features" i18nKey="ui.copy.365381da0351" />
            </Button>
          </Col>
        </Row>
        <div className="eeg-customization-panel__toolbar">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={loadOverrides}
            disabled={!recordingId.trim()}
          >
            <Trans ns="features" i18nKey="ui.copy.56e3badc4e6c" />
          </Button>
        </div>
        {loading ? (
          <Spinner animation="border" size="sm" />
        ) : rows.length === 0 ? (
          <Alert variant="light" className="mb-0">
            <Trans ns="features" i18nKey="ui.copy.925eee288c83" /></Alert>
        ) : (
          <Table bordered hover size="sm" className="mb-0">
            <thead>
              <tr>
                <th><Trans ns="features" i18nKey="ui.copy.879f0b1bef59" /></th>
                <th><Trans ns="features" i18nKey="ui.copy.5200bdfc01a1" /></th>
                <th><Trans ns="features" i18nKey="ui.copy.085ac9a633dc" /></th>
                <th><Trans ns="features" i18nKey="ui.copy.4ed1f46bbe04" /></th>
                <th><Trans ns="features" i18nKey="ui.copy.f2f8570ddd7b" /></th>
                <th><Trans ns="features" i18nKey="ui.copy.97c89a4d6630" /></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.channel_name}</td>
                  <td>{row.hp ?? "-"}</td>
                  <td>{row.lp ?? "-"}</td>
                  <td>{row.notch ?? "-"}</td>
                  <td>{toDateTimeLabel(row.updated_at)}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => deleteOverride(row.channel_name)}
                    >
                      <Trans ns="features" i18nKey="ui.copy.44c57abd888a" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};

export default FilterOverrideEditorPanel;
